import BACKEND_URL from "./apiConfig";
import { APP_VERSION } from "../version";

const API = BACKEND_URL;
const LOG_URL = `${API}/api/error-logs`;

// Dedupe: skip logging the same error message within 5 seconds.
// iter 392 — escalating: after 3 sends of the same key, the window grows
// to 10 minutes (idle overnight tabs were flooding the server with the
// same timeout error once per poll cycle, ~600 rows/night/machine).
const recentErrors = new Map();
const DEDUPE_MS = 5000;
const ESCALATED_DEDUPE_MS = 10 * 60 * 1000;
const ESCALATE_AFTER = 3;

function isDuplicate(key) {
  const now = Date.now();
  const entry = recentErrors.get(key);
  if (entry) {
    const windowMs = entry.count >= ESCALATE_AFTER ? ESCALATED_DEDUPE_MS : DEDUPE_MS;
    if (now - entry.ts < windowMs) return true;
    // Reset escalation if the error went quiet for over an hour
    const count = now - entry.ts > 60 * 60 * 1000 ? 1 : entry.count + 1;
    recentErrors.set(key, { ts: now, count });
  } else {
    recentErrors.set(key, { ts: now, count: 1 });
  }
  // Housekeep
  if (recentErrors.size > 50) {
    for (const [k, v] of recentErrors) {
      if (now - v.ts > ESCALATED_DEDUPE_MS) recentErrors.delete(k);
    }
  }
  return false;
}

// Circuit breaker state — if the error-logs endpoint itself starts failing
// (e.g. backend 503'ing), stop firing logs for a cooldown period. Without
// this, every client error during an outage triggers another POST which
// itself 503s, creating an error storm in the browser console.
let circuitOpenUntil = 0;
let consecutiveFailures = 0;
const CIRCUIT_TRIP_FAILURES = 3;
const CIRCUIT_COOLDOWN_MS = 60 * 1000; // 60s trip duration

function getUserContext() {
  return {
    user_id: localStorage.getItem("user_id") || null,
    user_name: localStorage.getItem("user_name") || null,
    user_role: localStorage.getItem("user_role") || null,
  };
}

function truncate(str, max = 1000) {
  if (!str) return null;
  const s = typeof str === "string" ? str : JSON.stringify(str);
  return s.length > max ? s.slice(0, max) + "...[truncated]" : s;
}

// iter 380d — Jake: "log instances like that so we can see exactly what
// caused it." Breadcrumb ring buffer of the user's last clicks + API
// calls, attached to every error log so Diagnostics shows the lead-up.
const MAX_CRUMBS = 25;
const crumbs = [];

export function addBreadcrumb(type, detail) {
  try {
    crumbs.push({
      t: new Date().toISOString().slice(11, 23),
      p: window.location.pathname,
      type,
      detail: String(detail || "").slice(0, 160),
    });
    if (crumbs.length > MAX_CRUMBS) crumbs.shift();
  } catch { /* never throw */ }
}

function installClickBreadcrumbs() {
  document.addEventListener(
    "click",
    (e) => {
      try {
        const el = e.target?.closest?.("[data-testid], button, a, [role='button'], select, input[type='checkbox']");
        if (!el) return;
        const label = el.getAttribute?.("data-testid")
          || (el.getAttribute?.("aria-label"))
          || (el.textContent || "").trim().slice(0, 50)
          || el.tagName;
        addBreadcrumb("click", label);
      } catch { /* never throw */ }
    },
    { capture: true, passive: true }
  );
}

/**
 * Log an error to the backend. Never throws.
 */
export async function logError({
  action,
  error,
  page,
  source = "manual",
  extra,
} = {}) {
  try {
    const err = error || {};
    const resp = err.response || {};
    const config = resp.config || err.config || {};
    const dedupeKey = `${action || ""}:${err.message || ""}:${config.url || ""}`;
    if (isDuplicate(dedupeKey)) return;

    // Circuit breaker — don't make things worse when the backend is
    // already failing. Silently drop logs while the circuit is open.
    if (Date.now() < circuitOpenUntil) return;

    const body = {
      ...getUserContext(),
      page: page || window.location.pathname,
      action: action || config.url?.split("/api/")[1]?.split("?")[0]?.replace(/\//g, "_") || "unknown",
      source,
      error_message: err.message || String(err),
      error_stack: err.stack?.slice(0, 2000) || null,
      status_code: resp.status || null,
      request_url: config.url || null,
      request_method: config.method?.toUpperCase() || null,
      request_payload: truncate(config.data),
      response_body: truncate(resp.data),
      duration_ms: config._startTime ? Date.now() - config._startTime : null,
      device_info: navigator.userAgent?.slice(0, 250) || null,
      extra: extra || null,
      // iter 380d — last user actions before the error
      breadcrumbs: crumbs.slice(-15),
      // iter 429 — troubleshooting context for the daily digest
      app_version: APP_VERSION || null,
      request_id: resp.headers?.["x-request-id"] || null,
      screen: typeof window !== "undefined" ? `${window.innerWidth}x${window.innerHeight}` : null,
      online: typeof navigator !== "undefined" ? navigator.onLine : null,
    };

    // Use fetch with keepalive for reliability. Track failures so the
    // circuit breaker can trip if the endpoint itself is down.
    // iter 375o — Include the session token: the AuthGate middleware
    // (iter 321) 401s unauthenticated POSTs, which silently killed ALL
    // frontend error logging in production since April.
    const token = (typeof localStorage !== "undefined" && localStorage.getItem("session_token")) || null;
    fetch(LOG_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
      keepalive: true,
    }).then((r) => {
      if (r.ok) {
        consecutiveFailures = 0;
        return;
      }
      consecutiveFailures += 1;
      if (consecutiveFailures >= CIRCUIT_TRIP_FAILURES) {
        circuitOpenUntil = Date.now() + CIRCUIT_COOLDOWN_MS;
        consecutiveFailures = 0;
      }
    }).catch(() => {
      consecutiveFailures += 1;
      if (consecutiveFailures >= CIRCUIT_TRIP_FAILURES) {
        circuitOpenUntil = Date.now() + CIRCUIT_COOLDOWN_MS;
        consecutiveFailures = 0;
      }
    });
  } catch {
    // Never throw from the logger
  }
}

/**
 * Install axios interceptor that auto-logs every failed request.
 * Call once at app init: installAxiosInterceptor(axios)
 */
export function installAxiosInterceptor(axiosInstance) {
  // Tag each request with a start time
  axiosInstance.interceptors.request.use((config) => {
    config._startTime = Date.now();
    // iter 380d — API breadcrumbs (skip the log endpoint itself)
    if (!(config.url || "").includes("/error-logs")) {
      addBreadcrumb("api", `${(config.method || "get").toUpperCase()} ${config.url || ""}`);
    }
    return config;
  });

  axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      // Skip logging the error-logs endpoint itself
      const url = error.config?.url || "";
      if (url.includes("/error-logs")) return Promise.reject(error);

      // iter 392 — never log synthetic/benign failures:
      //  * BackendDownError is OUR OWN circuit-breaker rejection — logging
      //    it to the very server it claims is down produced thousands of
      //    "Backend unreachable" rows in prod.
      //  * Canceled requests (navigation/query-key changes) are not errors.
      if (
        error?.isBackendDown ||
        error?.name === "BackendDownError" ||
        error?.name === "CanceledError" ||
        error?.code === "ERR_CANCELED" ||
        error?.message === "canceled"
      ) {
        return Promise.reject(error);
      }

      // iter 398i — DB-cost fix: pure client-side transport failures
      // (request timeout / network drop with NO server response) carry no
      // stack or server body, so they're worthless for debugging — but
      // each one POSTs a ~2.5KB row to Mongo. On a slow/busy backend that
      // write-storm feeds back into more slowness. Drop them at the source;
      // real HTTP errors (they have error.response) are still logged.
      const noResponse = !error?.response;
      const transportOnly =
        error?.code === "ECONNABORTED" ||
        error?.code === "ERR_NETWORK" ||
        error?.code === "ETIMEDOUT" ||
        error?.message === "Network Error" ||
        /^timeout of /i.test(error?.message || "");
      if (noResponse && transportOnly) {
        return Promise.reject(error);
      }

      logError({
        action: url.split("/api/")[1]?.split("?")[0]?.replace(/\//g, "_") || "api_call",
        error,
        source: "interceptor",
      });
      return Promise.reject(error);
    }
  );
}

/**
 * Install global window error handlers.
 * Catches unhandled errors and promise rejections.
 * Call once at app init: installGlobalHandlers()
 */
export function installGlobalHandlers() {
  installClickBreadcrumbs();
  window.addEventListener("error", (event) => {
    logError({
      action: "unhandled_error",
      error: event.error || new Error(event.message),
      source: "global",
      extra: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const err = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
    // iter 372k — benign axios cancellations (query key changed mid-
    // flight) shouldn't trip the dev overlay or the error log.
    if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
      event.preventDefault();
      return;
    }
    logError({
      action: "unhandled_promise_rejection",
      error: err,
      source: "global",
    });
  });
}
